const DEFAULT_CUSTOM_AUTH_DOMAIN = 'https://auth.raizcuadrada.com.mx';

function readEnvValue(key: string): string | undefined {
  const value = (import.meta as any)?.env?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function readWindowValue(key: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const value = (window as any)?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function withNoTrailingSlash(value?: string | null): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.replace(/\/+$/, '') : undefined;
}

function appendPath(base: string, path: string): string {
  const cleanBase = withNoTrailingSlash(base) ?? base;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${cleanBase}${cleanPath}`;
}

export function getSupabaseProjectUrl(): string | undefined {
  return withNoTrailingSlash(readEnvValue('VITE_SUPABASE_URL'));
}

export function getSupabaseAuthDomain(): string | undefined {
  return withNoTrailingSlash(
    readEnvValue('VITE_SUPABASE_CUSTOM_AUTH_DOMAIN')
      || readWindowValue('SUPABASE_CUSTOM_AUTH_DOMAIN')
      || readEnvValue('VITE_SUPABASE_CUSTOM_DOMAIN')
      || readWindowValue('SUPABASE_CUSTOM_DOMAIN')
      || DEFAULT_CUSTOM_AUTH_DOMAIN
  );
}

export function getSupabaseAuthUrl(): string | undefined {
  const domain = getSupabaseAuthDomain();
  if (!domain) return undefined;
  return /\/auth\/v1$/i.test(domain) ? domain : appendPath(domain, '/auth/v1');
}

export function getSupabaseStorageBaseUrl(): string | undefined {
  return withNoTrailingSlash(
    readEnvValue('VITE_SUPABASE_STORAGE_DOMAIN')
      || readWindowValue('SUPABASE_STORAGE_DOMAIN')
      || readEnvValue('VITE_SUPABASE_CUSTOM_DOMAIN')
      || readWindowValue('SUPABASE_CUSTOM_DOMAIN')
      || getSupabaseAuthDomain()
      || getSupabaseProjectUrl()
  );
}

export function normalizeSupabaseStorageUrl(raw?: string | null): string {
  if (!raw) return '';

  let value = raw.trim();
  if (!value) return '';

  if (value.startsWith('//')) {
    const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
    value = `${protocol}${value}`;
  }

  const base = getSupabaseStorageBaseUrl();
  if (!base) return value;

  try {
    const parsed = new URL(value);
    if (/^\/storage(\/|$)/.test(parsed.pathname)) {
      return `${base}${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return value;
  } catch {
    if (value.startsWith('storage/')) value = `/${value}`;
    if (value.startsWith('/storage/')) return `${base}${value}`;
    return value;
  }
}

export function getSupabaseEdgeBaseUrl(): string | undefined {
  const browserEdgeBase = readWindowValue('SUPABASE_EDGE_URL');
  if (browserEdgeBase) return withNoTrailingSlash(browserEdgeBase);

  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin.replace(/\/+$/, '')}/api/edge`;
  }

  return withNoTrailingSlash(readEnvValue('VITE_SUPABASE_EDGE_URL'));
}

export function buildEdgeFunctionUrl(fnName: string): URL | null {
  const edgeBase = getSupabaseEdgeBaseUrl();
  if (!edgeBase) return null;

  try {
    const resolvedBase = typeof window !== 'undefined'
      ? new URL(edgeBase, window.location.origin)
      : new URL(edgeBase);

    const basePath = `${resolvedBase.origin}${resolvedBase.pathname.replace(/\/+$/, '')}`;
    const useDirect = /\.functions\.supabase\.co$/i.test(resolvedBase.hostname) || !/\.supabase\.co$/i.test(resolvedBase.hostname);
    const target = useDirect
      ? `${basePath}/${fnName}`
      : `${basePath}/functions/v1/${fnName}`;

    return new URL(target);
  } catch {
    return null;
  }
}

export function requireEdgeFunctionUrl(fnName: string): string {
  const url = buildEdgeFunctionUrl(fnName);
  if (!url) {
    throw new Error('missing_edge_base');
  }
  return url.toString();
}

export function buildAuthCallbackUrl(searchParams?: Record<string, string | number | boolean | null | undefined>): string {
  if (typeof window === 'undefined' || !window.location?.origin) return '/auth/callback';

  const url = new URL('/auth/callback', window.location.origin);
  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      if (value === undefined || value === null || value === '') continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

export function buildPasswordUpdateUrl(): string {
  if (typeof window === 'undefined' || !window.location?.origin) return '/update-password';
  return new URL('/update-password', window.location.origin).toString();
}
